// Canonical product price logic — mirrors the backend Product::final_price
// (sale_price whenever it is set, otherwise the regular price).
//
// Use these helpers everywhere a product price is displayed or charged so the
// frontend and backend never disagree on the amount.

/** Regular (MRP) price as a number. */
export const getRegularPrice = (product) => {
  const p = Number(product?.price);
  return Number.isFinite(p) ? p : 0;
};

/** Sale price as a positive number, or null when not set. */
export const getSalePrice = (product) => {
  const sp = Number(product?.sale_price);
  return Number.isFinite(sp) && sp > 0 ? sp : null;
};

/**
 * Final price actually charged for the product.
 * Uses sale_price whenever it is set (even if it is not lower than the
 * regular price), matching the backend. Falls back to the regular price.
 */
export const getFinalPrice = (product) => {
  const sale = getSalePrice(product);
  return sale != null ? sale : getRegularPrice(product);
};

/**
 * True only when there is a genuine discount to advertise — i.e. the sale
 * price is set AND below the regular price. Drives the struck-through MRP.
 */
export const hasDiscount = (product) => {
  const sale = getSalePrice(product);
  return sale != null && getRegularPrice(product) > sale;
};

/** Discount percentage (0 when there is no genuine discount). */
export const getDiscountPercent = (product) => {
  if (!hasDiscount(product)) return 0;
  const price = getRegularPrice(product);
  const sale = getSalePrice(product);
  return Math.round(((price - sale) / price) * 100);
};

// ---------------------------------------------------------------------------
// Variant helpers — the backend serializes active/inactive variants under
// `product.variants`; the customer app only ever offers active ones.
// ---------------------------------------------------------------------------

/** Active variants for a product (empty array when none). */
export const getActiveVariants = (product) =>
  (Array.isArray(product?.variants) ? product.variants : []).filter(
    (v) => v && (v.status === undefined || v.status === 'active')
  );

/** True when the product has at least one selectable (active) variant. */
export const hasVariants = (product) => getActiveVariants(product).length > 0;

/**
 * Effective price of a single variant, mirroring the backend's
 * ProductVariant::final_price (variant sale_price → variant price → parent).
 */
export const getVariantFinalPrice = (variant, product) => {
  if (variant == null) return getFinalPrice(product);
  const explicit = Number(variant.final_price);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const sale = Number(variant.sale_price);
  if (Number.isFinite(sale) && sale > 0) return sale;
  const price = Number(variant.price);
  if (Number.isFinite(price) && price > 0) return price;
  return getFinalPrice(product);
};

/**
 * Lowest variant price for a product, used for the "from ₹X" card hint.
 * Returns null when the product has no active variants.
 */
export const getVariantPriceFrom = (product) => {
  const variants = getActiveVariants(product);
  if (variants.length === 0) return null;
  return variants.reduce((min, v) => {
    const price = getVariantFinalPrice(v, product);
    return min == null || price < min ? price : min;
  }, null);
};
